import { CheckOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import type { WorkspaceAppearance, WorkspaceCabinet, WorkspacePayload, WorkspaceSection } from "@wms/domain";
import { antdTheme } from "@wms/ui";
import { Alert, App, Button, ConfigProvider, Input, Modal, Skeleton, Typography, theme as antTheme } from "antd";
import type { ThemeConfig } from "antd";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";
import { HelpHint } from "../components/HelpHint";
import { OperationTag } from "../components/OperationTag";
import { usePortalStore } from "../portal/store";
import { useUiStore } from "../ui/theme";
import { type ActiveLook, themeWithLook, useLookStore } from "./look";
import {
  DEFAULT_APPEARANCE,
  FALLBACK_PALETTE,
  STANDARD_BACKGROUND,
  STANDARD_CANVAS,
  lookProblems,
  lookTokens,
  mergePalette,
  resolveAppearance,
  sameAppearance,
} from "./palette";
import { conflictOf, useWorkspace } from "./useWorkspace";
import "./appearance.css";

interface SwatchItem {
  id: string;
  label: string;
}

/**
 * UX04: a group of swatches with the semantics of radio buttons — one tab stop, arrows move the choice, Home/End jump
 * to the ends, Space/Enter choose; the name is always written under or on the swatch.
 */
function SwatchGroup<T extends SwatchItem>({
  labelledBy,
  items,
  value,
  onChange,
  render,
  className,
  testid,
}: {
  labelledBy: string;
  items: T[];
  value: string;
  onChange: (id: string) => void;
  render: (item: T, checked: boolean) => ReactNode;
  className: string;
  testid: string;
}) {
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const chosen = items.findIndex((item) => item.id === value);
  const move = (to: number) => {
    const index = (to + items.length) % items.length;
    onChange(items[index].id);
    refs.current[index]?.focus();
  };
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className={`wms-swatches ${className}`} data-testid={testid}>
      {items.map((item, index) => {
        const checked = index === chosen;
        return (
          <div
            key={item.id}
            ref={(el) => {
              refs.current[index] = el;
            }}
            role="radio"
            aria-checked={checked}
            aria-label={item.label}
            tabIndex={checked || (chosen < 0 && index === 0) ? 0 : -1}
            className={`wms-swatch${checked ? " is-checked" : ""}`}
            data-testid={`${testid}-${item.id}`}
            onClick={() => onChange(item.id)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") move(index + 1);
              else if (e.key === "ArrowLeft" || e.key === "ArrowUp") move(index - 1);
              else if (e.key === "Home") move(0);
              else if (e.key === "End") move(items.length - 1);
              else if (e.key === " " || e.key === "Enter") onChange(item.id);
              else return;
              e.preventDefault();
            }}
          >
            {render(item, checked)}
          </div>
        );
      })}
    </div>
  );
}

const CHECK = (
  <span className="wms-swatch-check" aria-hidden>
    <CheckOutlined />
  </span>
);

/**
 * ТЗ «Рабочие столы и персонализация» 31.10.2026 (UXW-14, UXW-16, UXW-17, CW-08), экран UX04, путь FL01: the own
 * background and button colour of the signed-in person in this cabinet. The choice shows on the whole page at once but
 * stays local until «Сохранить»; «Отмена» returns the saved look, «По умолчанию» shows the standard of the cabinet and
 * is applied by «Сохранить» too; «Сохранено» appears only after the server answered. Leaving the screen without
 * saving returns the saved look. Business data, roles and rights are never touched.
 */
export function AppearanceSettings({ cabinet }: { cabinet: WorkspaceCabinet }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const staffId = useAuthStore((s) => s.me?.user.id);
  const clientId = usePortalStore((s) => s.me?.user.id);
  const principal = cabinet === "portal" ? clientId : staffId;
  const dark = useUiStore((s) => s.theme) === "dark";
  const workspace = useWorkspace(cabinet, true);
  const data = workspace.data;
  const saved = data?.workspace.appearance;
  const standard = data?.standard.appearance ?? DEFAULT_APPEARANCE[cabinet];
  const [draft, setDraft] = useState<WorkspaceAppearance | null>(null);
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [failure, setFailure] = useState<string | null>(null);
  const [conflict, setConflict] = useState<WorkspaceSection | null>(null);
  const current = draft ?? saved ?? standard;
  const dirty = draft !== null && !sameAppearance(draft, saved ?? standard);
  const ids = useId();

  // UXW-16: the unsaved choice is shown on the whole page; leaving the screen gives the saved look back
  useEffect(() => {
    if (!principal) return;
    useLookStore.getState().setPreview(dirty && draft ? { cabinet, principal, appearance: draft } : null);
  }, [cabinet, principal, dirty, draft]);
  useEffect(() => () => useLookStore.getState().setPreview(null), []);

  if (workspace.isError) {
    return (
      <Alert
        type="warning"
        showIcon
        data-testid="appearance-load-failed"
        message="Не удалось загрузить оформление"
        description={`${errorMessage(workspace.error)}. Сейчас показан стандартный вид кабинета.`}
        action={<Button onClick={() => workspace.refetch()}>Повторить</Button>}
      />
    );
  }
  if (!data) return <Skeleton active paragraph={{ rows: 6 }} />;

  const palette = data.palette ?? FALLBACK_PALETTE;
  const resolved = resolveAppearance(current, cabinet, mergePalette(palette));
  const problems = lookProblems(resolved);
  const tokens = lookTokens(resolved);
  const sample: ActiveLook = { ...resolved, cabinet, principal: principal ?? "", tokens, previewing: true };
  // the sample always shows the light look: in the dark theme it does not inherit the dark surfaces
  const sampleTheme: ThemeConfig = dark
    ? { ...themeWithLook(antdTheme, sample), algorithm: antTheme.defaultAlgorithm, inherit: false }
    : themeWithLook({}, sample);
  const backgrounds = [
    // «Стандартный» of staff is the neutral background of today; the cabinet of a client starts from lilac
    ...(cabinet === "staff" ? [{ id: STANDARD_BACKGROUND, label: "Стандартный", hex: STANDARD_CANVAS }] : []),
    ...palette.backgrounds,
  ];
  const choose = (part: keyof WorkspaceAppearance) => (id: string) => {
    setStatus("idle");
    setDraft({ ...current, [part]: id });
  };

  const save = (revision: number) => {
    setStatus("idle");
    setFailure(null);
    // «По умолчанию» is stored as «the standard of the cabinet», not as a copy of today's ids
    const sent = { background: current.background, accent: current.accent };
    const appearance = sameAppearance(sent, standard) ? null : sent;
    workspace.save.mutate(
      { revision, appearance },
      {
        onSuccess: () => {
          // a swatch chosen while the answer was on its way stays as the new unsaved choice
          setDraft((now) => (now && !sameAppearance(now, sent) ? now : null));
          setStatus("saved");
          message.success("Оформление сохранено");
        },
        onError: (error) => {
          const theirs = conflictOf(error);
          if (theirs) {
            setConflict(theirs);
            return;
          }
          // CW-08: the swatches stay as chosen, the button may be pressed again
          setStatus("error");
          setFailure(errorMessage(error));
        },
      },
    );
  };
  const loadTheirs = () => {
    if (!conflict) return;
    queryClient.setQueryData<WorkspacePayload>(workspace.key, (old) => (old ? { ...old, saved: true, workspace: conflict } : old));
    setConflict(null);
    setDraft(null);
    setStatus("idle");
    void workspace.refetch();
  };
  const saveMine = () => {
    if (!conflict) return;
    const revision = conflict.revision;
    setConflict(null);
    save(revision);
  };
  const theirs = conflict ? resolveAppearance(conflict.appearance, cabinet, mergePalette(palette)) : null;

  const statusText = dirty
    ? "Предварительный просмотр: выбор ещё не сохранён. «Отмена» вернёт прежний вид."
    : status === "saved"
      ? null
      : data.saved
        ? "Показан сохранённый вид. Выберите другой фон или цвет кнопок, чтобы сохранить новый."
        : "Показан стандартный вид кабинета. Выберите фон или цвет кнопок, чтобы сохранить свой.";
  const saveBlocked = problems.length > 0;

  return (
    <div className="wms-look" data-testid="appearance-settings">
      <div>
        <h2 className="wms-look-head">
          Как оформить ваш кабинет?
          <HelpHint
            subject="Оформление"
            text="Фон страницы и цвет основных кнопок — только для вас и только в этом кабинете. Документы, остатки, роли и права не меняются; другие пользователи видят своё оформление."
          />
        </h2>
        <p className="wms-look-note">Фон и кнопки можно выбрать отдельно. Предварительный просмотр — до сохранения.</p>
        {dark && (
          <Alert
            type="info"
            showIcon
            data-testid="appearance-dark-note"
            message="Сейчас включена тёмная тема"
            description="Выбранный фон и цвет кнопок действуют в светлой теме; тёмная тема остаётся как есть. Образец ниже показывает светлый вид."
          />
        )}
      </div>

      <section>
        <h3 className="wms-look-head" id={`${ids}-bg`}>
          Фон
          <HelpHint
            subject="Фон"
            text="Цвет страницы под карточками. Карточки, таблицы, поля и печатные документы остаются белыми, текст — тёмным."
          />
        </h3>
        <p className="wms-look-note">Спокойный цвет страницы. Карточки и таблицы остаются белыми.</p>
        <SwatchGroup
          labelledBy={`${ids}-bg`}
          items={backgrounds}
          value={resolved.background}
          onChange={choose("background")}
          className="wms-swatches-bg"
          testid="appearance-bg"
          render={(item, checked) => (
            <>
              <span className="wms-swatch-tile" style={{ background: item.hex }}>
                {checked ? CHECK : null}
              </span>
              <span className="wms-swatch-name">{item.label}</span>
            </>
          )}
        />
      </section>

      <section>
        <h3 className="wms-look-head" id={`${ids}-accent`}>
          Цвет кнопок
          <HelpHint
            subject="Цвет кнопок"
            text="Основные кнопки, ссылки, выбранный пункт меню и рамка фокуса. Цвета поступления, отгрузки, перемещения и списания, статусы, ошибки и штрихкоды не меняются."
          />
        </h3>
        <p className="wms-look-note">Основная кнопка, ссылки и выбранный пункт меню. Цвета операций и ошибок не меняются.</p>
        <SwatchGroup
          labelledBy={`${ids}-accent`}
          items={palette.accents}
          value={resolved.accent}
          onChange={choose("accent")}
          className="wms-swatches-accent"
          testid="appearance-accent"
          render={(item, checked) => (
            <span className="wms-swatch-tile wms-swatch-accent" style={{ background: item.hex, color: item.text }}>
              <span>{item.label}</span>
              {checked ? CHECK : null}
            </span>
          )}
        />
      </section>

      <section>
        <h3 className="wms-look-head">
          Образец
          <HelpHint
            subject="Образец"
            text="Так будут выглядеть кнопки, поле, ссылка и подсказки после сохранения. Недоступная кнопка, ошибка и цвета операций показаны, чтобы было видно: их смысл и цвет не меняются."
          />
        </h3>
        <ConfigProvider theme={sampleTheme}>
          <div className="wms-look-preview" style={{ background: resolved.canvas ?? STANDARD_CANVAS }} data-testid="appearance-preview">
            <div className="wms-look-preview-card">
              <div className="wms-look-preview-title">
                Поступление A000041
                <HelpHint subject="Образец подсказки" text="Подсказка «(?)» открывается нажатием, клавишами Enter и Пробел; закрывается Escape." />
              </div>
              <div className="wms-look-preview-row">
                <Button type="primary" tabIndex={-1}>
                  Сохранить
                </Button>
                <Input value="Коробка, 12 шт" readOnly aria-label="Образец поля" style={{ width: 200 }} tabIndex={-1} />
                <Typography.Link href="#" tabIndex={-1} onClick={(e) => e.preventDefault()}>
                  Открыть документ
                </Typography.Link>
              </div>
              <div className="wms-look-preview-row">
                <Button disabled>Подтвердить план</Button>
                <span className="wms-look-preview-reason">Недоступно: сначала добавьте товары.</span>
              </div>
              <Typography.Text type="danger">Товары → строка 3 → Количество: доступно 8 шт, указано 10. Уменьшите количество.</Typography.Text>
              <div className="wms-look-preview-row" aria-label="Цвета операций не меняются">
                <OperationTag kind="receive">Поступление</OperationTag>
                <OperationTag kind="ship">Отгрузка</OperationTag>
                <OperationTag kind="move">Перемещение</OperationTag>
                <OperationTag kind="writeoff">Списание</OperationTag>
              </div>
            </div>
            <p className="wms-look-preview-note">Ошибка остаётся красной. Штрихкод — чёрным на белом. Цвета операций не меняются.</p>
          </div>
        </ConfigProvider>
      </section>

      {saveBlocked && (
        <Alert
          type="warning"
          showIcon
          data-testid="appearance-contrast"
          message="Такое сочетание не сохранить: текст будет плохо виден"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {problems.map((p) => (
                <li key={p.code}>{p.text}</li>
              ))}
            </ul>
          }
        />
      )}
      {status === "error" && (
        <Alert
          type="error"
          showIcon
          data-testid="appearance-error"
          message="Не удалось сохранить оформление. Выбор сохранён на этом экране — попробуйте ещё раз"
          description={failure ?? undefined}
        />
      )}

      <div className="wms-look-actions">
        <Button
          size="large"
          data-testid="appearance-default"
          onClick={() => {
            setStatus("idle");
            setFailure(null);
            setDraft({ background: standard.background, accent: standard.accent });
          }}
        >
          По умолчанию
        </Button>
        <span className="wms-look-spacer" />
        <Button
          size="large"
          data-testid="appearance-cancel"
          disabled={!dirty}
          onClick={() => {
            setDraft(null);
            setStatus("idle");
            setFailure(null);
          }}
        >
          Отмена
        </Button>
        <Button
          size="large"
          type="primary"
          data-testid="appearance-save"
          disabled={!dirty || saveBlocked}
          loading={workspace.save.isPending}
          onClick={() => save(data.workspace.revision)}
        >
          Сохранить
        </Button>
      </div>
      <div aria-live="polite">
        {status === "saved" ? (
          <Typography.Text type="success" strong data-testid="appearance-saved">
            Сохранено. Оформление восстановится при следующем входе и на другом устройстве.
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary" data-testid="appearance-status">
            {saveBlocked ? "Кнопка «Сохранить» недоступна: выберите другое сочетание (причина выше)." : statusText}
          </Typography.Text>
        )}
      </div>

      <Modal
        open={conflict !== null}
        title="Оформление изменено в другом окне"
        onCancel={() => setConflict(null)}
        footer={[
          <Button key="load" data-testid="appearance-conflict-load" onClick={loadTheirs}>
            Загрузить новый вид
          </Button>,
          <Button key="mine" type="primary" data-testid="appearance-conflict-mine" onClick={saveMine}>
            Сохранить мой вариант
          </Button>,
        ]}
      >
        <p data-testid="appearance-conflict">
          Пока вы выбирали, оформление сохранили в другом окне или на другом устройстве
          {theirs ? `: фон «${theirs.backgroundLabel}», кнопки «${theirs.accentLabel}»` : ""}.
        </p>
        <p style={{ marginBottom: 0 }}>
          «Загрузить новый вид» покажет сохранённый там вариант. «Сохранить мой вариант» запишет ваш выбор поверх него. Рабочий стол при этом не
          меняется.
        </p>
      </Modal>
    </div>
  );
}
